import React, { useEffect, useState, useRef } from 'react';
import styles from './LocationReportList.module.scss';

import {
  NoDataTableRow,
  Pagination,
  SearchField,
  SummaryHeaderWrapper,
  SummaryPageHeader,
  SummaryTableControls,
  SummaryWrapper,
  TableWrapper
} from 'components/SummaryComponents';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import TableHeadComponent from 'components/SummaryComponents/TableHeadComponent/TableHeadComponent';
import EditabilityToggle from 'components/EditabilityToggle/EditabilityToggle';
import { FilterButton, FilterChips, FilterWrapper } from 'components/FilterComponentsV2';
import { StyledButton } from 'components/Buttons';
import { AddGeneralReportPopup } from 'components/Popups';
import StyledLink from 'components/StyledLink';

import { IconButton, Table, TableBody } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';

import { useMobileViewport } from 'hooks/useMobileViewport';
import { useUserConfig } from 'hooks/useUserConfig';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import { useReportActions, useReportSelector } from 'hooks/ReportList';
import { useLocation, useHistory } from 'react-router-dom';
import useScrollManager from 'hooks/useScrollManager';

import {
  getChangedByApplyFilter,
  getChangedByLimitFilter,
  getChangedByPageFilter,
  getChangedBySearchFilter,
  getChangedBySortFilter
} from 'helpers/SummaryPagesHelpers';
import { formatDate, fileDownload, cloneObj, getUserFullName } from 'helpers/AppHelpers';

import {
  LOCATION_REPORT_EDIT_PATH,
  LOCATION_DETAILED_PATH,
  PROJECT_DETAILED_PATH
} from 'constants/routeConstants';
import { LOCATION_REPORTS_TABLE_PARAMETER } from 'constants/configTableConstants';

import fileIcon from 'assets/images/fileIcon.svg';

import { tableHeadConfig } from './tableHeadConfig';

import DownloadArchiveButton from '../commonComponents/DownloadArchiveButton';
import PRJPlate from '../commonComponents/PRJPlate';
import FilterModal from '../Filter';
import { generateChips, removeChip } from '../helpers';

const defaultFilter = {
  filters: {
    generalReportTypes: ['Location'],
    locationJobNumberIds: [],
    locationIds: [],
    assignedUserIds: [],
    generalReportTemplateIds: [],
    dateFrom: '',
    dateTo: '',
    searchQuery: '',
    showAssigned: false,
    showNotSubmitted: false,
    showWithInactiveLocations: false
  },
  pagination: {
    page: 1,
    totalPages: 1,
    limit: 10
  },
  sortRules: [],
  responseReceived: false
};

export default function LocationReportList() {
  const isMobile = useMobileViewport();
  const history = useHistory();
  const tableRef = useRef(null);
  const location = useLocation();

  const [openFilter, setOpenFilter] = useState(false);
  const [tableState, setTableState] = useState([]);
  const [openAddReportPopup, setAddReportPopup] = useState(false);
  const [chips, setChips] = useState([]);

  const { isConfigReceived, getTableLimit, isAllLimitEnabled } = useUserConfig();
  const { setLoadMoreFlagAction } = useCommonActions();
  const { loadMoreFlag } = useCommonSelector();

  const {
    setFilterAction,
    getReportsAction,
    updateReportAction,
    getPdfResourceAction,
    getFilterCriteriaAction,
    clearStateAction
  } = useReportActions();
  const { locationReportsFilter, filterCriteria } = useReportSelector();

  const query = new URLSearchParams(location.search);
  const shouldOpenPopup = query.get('shouldOpenPopup');
  const searchValue = query.get('searchQuery');

  const { filters, pagination, sortRules, responseReceived } = locationReportsFilter;

  const { saveScrollPosition, removeScrollPosition } = useScrollManager({
    tableKey: LOCATION_REPORTS_TABLE_PARAMETER,
    isDataLoaded: responseReceived
  });

  const generateInitialQuery = () => {
    return {
      filters: { ...defaultFilter.filters, ...filters, searchQuery: searchValue || '' },
      pagination: {
        page:
          getTableLimit(LOCATION_REPORTS_TABLE_PARAMETER) === 100 &&
          isAllLimitEnabled(LOCATION_REPORTS_TABLE_PARAMETER)
            ? defaultFilter.pagination.page
            : pagination?.page || defaultFilter.pagination.page,
        limit: getTableLimit(LOCATION_REPORTS_TABLE_PARAMETER)
      },
      sortRules: sortRules || defaultFilter.sortRules,
      responseReceived: false
    };
  };

  const getReports = (query, isAutoload) => {
    setFilterAction(query);

    const payload = cloneObj(query);
    payload.filters.createdUserList = [];

    getReportsAction(payload).then((tableData) => {
      setReports(tableData, query, isAutoload);
      !loadMoreFlag && setLoadMoreFlagAction(false);
    });
  };

  const setReports = (data, query, isAutoload) => {
    if (!data) {
      setFilterAction({
        ...query,
        responseReceived: true
      });
      return;
    }
  
    if (!isAutoload) {
      setTableState(data.items || []);
    } else {
      setTableState(tableState.concat(data.items || []));
    }
  
    const updatedFilter = {
      ...query,
      pagination: {
        limit: data.limit,
        page: data.pageNumber,
        totalPages: data.totalPages
      },
      responseReceived: true
    };
  
    setFilterAction(updatedFilter);
  };
  

  useEffect(() => {
    if (shouldOpenPopup === 'true') {
      setAddReportPopup(true);
      query.delete('shouldOpenPopup');
      history.replace({ search: query.toString() });
    }
  }, [shouldOpenPopup]);

  useEffect(() => {
    if (!isConfigReceived) return;

    getFilterCriteriaAction({ generalReportType: 'Location' }).then((criteria) => {
      const resultFilter = generateInitialQuery();
      getReports(resultFilter);

      const options = {
        ...criteria,
        createdUserList: filters?.createdUserList || []
      };

      setChips(generateChips(options, resultFilter.filters));
    });
  }, [isConfigReceived]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const onFileDownload = (id) => {
    getPdfResourceAction(id).then((res) => {
      if (isMobile) {
        fileDownload(res).then(() => console.log());
      } else {
        window.open(res.link, '_blank');
      }
    });
  };

  const findMatches = (value) => getReports(getChangedBySearchFilter(locationReportsFilter, value));
  const sortTable = (rowName) => getReports(getChangedBySortFilter(locationReportsFilter, rowName));
  const changePage = (page, param) =>
    getReports(getChangedByPageFilter(locationReportsFilter, page), param === 'AUTOLOAD');
  const applyFilter = (values) => {
    getReports(getChangedByApplyFilter(locationReportsFilter, values));

    const options = {
      ...filterCriteria,
      createdUserList: values?.createdUserList || []
    };

    setChips(generateChips(options, values));
  };
  const changeLimit = () =>
    getReports(
      getChangedByLimitFilter(
        locationReportsFilter,
        getTableLimit(LOCATION_REPORTS_TABLE_PARAMETER)
      )
    );

  const handleCellUpdate = ({ name, value, index, onClose }) => {
    const report = tableState[index];
    report[name] = value;
    updateReportAction(report).then((res) => {
      if (res.status !== 200) return;
      onClose();
      const tableData = cloneObj(tableState);
      tableData[index][name] = value;
      setTableState(tableData);
    });
  };

  const handleIdClick = ({ id, submitted }) => {
    if (submitted) {
      onFileDownload(id);
    } else {
      handleEditClick(id);
    }
  };

  const openFilterModal = () => setOpenFilter(true);

  const deleteChip = (chip) => applyFilter(removeChip(filters, chip));

  const handleEditClick = (rowId) => {
    saveScrollPosition();
    history.push(`${LOCATION_REPORT_EDIT_PATH}/${rowId}`);
  };

  const handleAddReportPopup = () => {
    setAddReportPopup(true);
    removeScrollPosition();
  };

  return (
    <SummaryWrapper>
      <SummaryHeaderWrapper>
        <SummaryPageHeader title="Location report">
          {!isMobile && (
            <StyledButton
              label="Add new report"
              onClick={handleAddReportPopup}
              classes={styles.main_button}
            />
          )}
        </SummaryPageHeader>
        <SummaryTableControls>
          <FilterWrapper>
            <FilterButton onClick={openFilterModal} isActive={openFilter || !!chips?.length} />
            {!isMobile && <FilterChips chips={chips} onDelete={deleteChip} />}
          </FilterWrapper>
          <div className={styles.actions}>
            <SearchField filterValue={filters?.searchQuery} onSearch={findMatches} />
            {!isMobile && <DownloadArchiveButton data={tableState.slice(-100) || []} />}
          </div>
        </SummaryTableControls>
      </SummaryHeaderWrapper>

      {isMobile && (
        <StyledButton
          label="Add new report"
          onClick={handleAddReportPopup}
          classes={styles.main_button}
        />
      )}

      <TableWrapper ref={tableRef}>
        <Table stickyHeader={!isMobile}>
          <TableHeadComponent
            config={tableHeadConfig}
            sortRules={sortRules}
            onSortApply={sortTable}
          />
          <TableBody>
            {tableState?.length ? (
              tableState.map((row, index) => (
                <StyledTableRow key={row.id} className={styles.location_report_row}>
                  <StyledTableCell className={styles.idCell}>
                    <span onClick={() => handleIdClick(row)} className={styles.clickable_id}>
                      {row.id || ''}
                    </span>
                  </StyledTableCell>
                  <StyledTableCell className={styles.reportTypeCell}>
                    {row?.project?.id ? (
                      <div className={styles.project_cell}>
                        <StyledLink
                          to={`${PROJECT_DETAILED_PATH}/${row.project?.id}`}
                          onClick={saveScrollPosition}>
                          {row.project?.name || ''}
                        </StyledLink>
                        <PRJPlate />
                      </div>
                    ) : (
                      <StyledLink
                        to={`${LOCATION_DETAILED_PATH}/${row.location?.id}`}
                        isRedirectAvailable={!row.location?.deleted}
                        onClick={saveScrollPosition}>
                        {row.location?.siteCode || ''}
                      </StyledLink>
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={styles.locationJobNumberCell}>
                    {row?.location?.locationJobNumber || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.jobNumberCell}>
                    {row.jobNumber || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.createdByCell}>
                    {getUserFullName(row?.createdByUser)}
                  </StyledTableCell>
                  <StyledTableCell className={styles.createdAtCell}>
                    {row.createdAtUtc ? formatDate(row.createdAtUtc) : ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.submittedByCell}>
                    {getUserFullName(row?.submittedByUser)}
                  </StyledTableCell>
                  <StyledTableCell className={styles.templateCell}>
                    {row.generalReportTemplate?.name || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.noteCell}>
                    <EditabilityToggle
                      name="note"
                      inputValue={row?.note || ''}
                      options={{
                        max: 250,
                        type: 'text',
                        index,
                        keyToDown: 'Enter',
                        multiline: true
                      }}
                      onAccept={handleCellUpdate}
                      isDisabled={!row.submitted}
                    />
                  </StyledTableCell>
                  <StyledTableCell className={styles.controlsCell}>
                    <div className={styles.controls}>
                      {row.submitted ? (
                        <>
                          <IconButton
                            onClick={() => onFileDownload(row.id)}
                            className={styles.icon_button}>
                            <img src={fileIcon} alt="file" />
                          </IconButton>
                          {row.isEditable && (
                            <IconButton
                              onClick={() => handleEditClick(row.id)}
                              className={styles.icon_button}>
                              <CreateIcon />
                            </IconButton>
                          )}
                        </>
                      ) : (
                        <span
                          className={styles.editingSpan}
                          onClick={() => handleEditClick(row.id)}>
                          Editing
                        </span>
                      )}
                    </div>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : responseReceived ? (
              <NoDataTableRow isTableFiltered={!!chips?.length || !!filters?.searchQuery} />
            ) : null}
          </TableBody>
        </Table>
      </TableWrapper>

      <Pagination
        withOptionAll
        rowsNumber={tableState?.length}
        currentPage={pagination?.page}
        totalPages={pagination?.totalPages}
        onChangePage={changePage}
        onChangeLimit={changeLimit}
        selectedLimit={pagination?.limit}
        tableName={LOCATION_REPORTS_TABLE_PARAMETER}
        tableRef={tableRef}
      />

      {openAddReportPopup && (
        <AddGeneralReportPopup
          open={openAddReportPopup}
          setOpen={setAddReportPopup}
          reportType="Location"
        />
      )}

      {openFilter && (
        <FilterModal
          open={openFilter}
          setOpen={setOpenFilter}
          onApply={applyFilter}
          filter={locationReportsFilter}
          reportType="Location"
        />
      )}
    </SummaryWrapper>
  );
}
